import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet';
import Header from '../../components/ui/Header';
import Sidebar from '../../components/ui/Sidebar';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import SearchBar from './components/SearchBar';
import TrendingStocks from './components/TrendingStocks';
import MarketMovers from './components/MarketMovers';
import FilterPanel from './components/FilterPanel';
import SearchResults from './components/SearchResults';
import StockDetailModal from './components/StockDetailModal';
import RecentSearches from './components/RecentSearches';

const StockSearchDiscovery = () => {
  const [searchQuery, setSearchQuery] = useState('');
  const [filters, setFilters] = useState({
    priceRange: { min: '', max: '' },
    marketCap: '',
    sector: '',
    exchange: '',
    dividendYield: { min: '', max: '' },
    peRatio: { min: '', max: '' },
    volume: ''
  });
  const [isFilterOpen, setIsFilterOpen] = useState(false);
  const [selectedStock, setSelectedStock] = useState(null);
  const [isStockModalOpen, setIsStockModalOpen] = useState(false);
  const [watchlistMessage, setWatchlistMessage] = useState('');
  const [showResults, setShowResults] = useState(false);

  // Handle search
  const handleSearch = (query) => {
    setSearchQuery(query);
    setShowResults(true);
    
    // Save to recent searches
    const recentSearches = JSON.parse(localStorage.getItem('recentStockSearches') || '[]');
    const updatedSearches = [query, ...recentSearches.filter(s => s !== query)].slice(0, 5);
    localStorage.setItem('recentStockSearches', JSON.stringify(updatedSearches));
  };

  // Handle stock selection
  const handleStockSelect = (stock) => {
    setSelectedStock(stock);
    setIsStockModalOpen(true);
  };

  // Handle filter application
  const handleApplyFilters = (newFilters) => {
    setFilters(newFilters);
    setShowResults(true);
  };

  // Handle add to watchlist
  const handleAddToWatchlist = (stock) => {
    // Mock watchlist functionality
    const watchlist = JSON.parse(localStorage.getItem('stockWatchlist') || '[]');
    const isAlreadyInWatchlist = watchlist.some(item => item.symbol === stock.symbol);
    
    if (!isAlreadyInWatchlist) {
      const updatedWatchlist = [...watchlist, stock];
      localStorage.setItem('stockWatchlist', JSON.stringify(updatedWatchlist));
      setWatchlistMessage(`${stock.symbol} added to watchlist!`);
    } else {
      setWatchlistMessage(`${stock.symbol} is already in your watchlist.`);
    }
    
    setTimeout(() => setWatchlistMessage(''), 3000);
  };

  // Handle search from recent searches
  const handleSearchSelect = (searchTerm) => {
    setSearchQuery(searchTerm);
    setShowResults(true);
  };

  // Clear search and results
  const handleClearSearch = () => {
    setSearchQuery('');
    setShowResults(false);
    setFilters({
      priceRange: { min: '', max: '' },
      marketCap: '',
      sector: '',
      exchange: '',
      dividendYield: { min: '', max: '' },
      peRatio: { min: '', max: '' },
      volume: ''
    });
  };

  return (
    <div className="min-h-screen bg-background">
      <Helmet>
        <title>Stock Search & Discovery - StockTracker Pro</title>
        <meta name="description" content="Discover and research stocks with advanced search and filtering capabilities. Find investment opportunities with real-time data and comprehensive analysis." />
      </Helmet>

      <Header />
      <Sidebar />

      <main className="relative z-0 w-full min-h-screen pt-16 overflow-x-hidden lg:ml-64">
        <div className="w-full max-w-full py-8 px-4 sm:px-6 lg:px-8">
          {/* Page Header */}
          <div className="mb-8 w-full">
            <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between mb-4 w-full">
              <div>
                <h1 className="text-3xl font-bold text-foreground">Stock Search & Discovery</h1>
                <p className="text-muted-foreground mt-2">
                  Find and research investment opportunities with advanced search and filtering
                </p>
              </div>
              {/* Mobile Filter Button */}
              <Button
                variant="outline"
                onClick={() => setIsFilterOpen(true)}
                className="lg:hidden w-full sm:w-auto"
              >
                <Icon name="Filter" size={20} className="mr-2" />
                Filters
              </Button>
            </div>
            {/* Search Bar */}
            <div className="w-full">
              <SearchBar 
                onSearch={handleSearch}
                onStockSelect={handleStockSelect}
              />
            </div>
            {/* Active Filters Display */}
            {(searchQuery || Object.values(filters).some(f => f !== '' && f !== null)) && (
              <div className="flex flex-wrap items-center gap-2 mt-4 w-full">
                <span className="text-sm text-muted-foreground">Active filters:</span>
                {searchQuery && (
                  <div className="flex items-center bg-primary/10 text-primary rounded-full px-3 py-1 text-sm">
                    Search: "{searchQuery}"
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => setSearchQuery('')}
                      className="h-4 w-4 ml-2"
                    >
                      <Icon name="X" size={12} />
                    </Button>
                  </div>
                )}
                {Object.entries(filters).map(([key, value]) => {
                  if (typeof value === 'object' && value !== null) {
                    const hasValue = value.min || value.max;
                    if (hasValue) {
                      return (
                        <div key={key} className="flex items-center bg-secondary/10 text-secondary rounded-full px-3 py-1 text-sm">
                          {key}: {value.min && `₹{value.min}`}{value.min && value.max && '-'}{value.max && `₹{value.max}`}
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => handleFilterChange(key, { min: '', max: '' })}
                            className="h-4 w-4 ml-2"
                          >
                            <Icon name="X" size={12} />
                          </Button>
                        </div>
                      );
                    }
                  } else if (value) {
                    return (
                      <div key={key} className="flex items-center bg-secondary/10 text-secondary rounded-full px-3 py-1 text-sm">
                        {key}: {value}
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => setFilters(prev => ({ ...prev, [key]: '' }))}
                          className="h-4 w-4 ml-2"
                        >
                          <Icon name="X" size={12} />
                        </Button>
                      </div>
                    );
                  }
                  return null;
                })}
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={handleClearSearch}
                  className="text-muted-foreground hover:text-foreground"
                >
                  Clear All
                </Button>
              </div>
            )}
          </div>
          {/* Responsive flex layout for sidebar and main content */}
          <div className="flex flex-col lg:flex-row gap-6 w-full">
            {/* Desktop Filter Sidebar */}
            <div className="hidden lg:block w-80 flex-shrink-0">
              <FilterPanel
                isOpen={true}
                onClose={() => {}}
                onApplyFilters={handleApplyFilters}
                currentFilters={filters}
              />
            </div>
            {/* Main Content */}
            <div className="flex-1 flex flex-col gap-6 w-full">
              {showResults ? (
                <SearchResults
                  searchQuery={searchQuery}
                  filters={filters}
                  onStockSelect={handleStockSelect}
                  onAddToWatchlist={handleAddToWatchlist}
                />
              ) : (
                <>
                  <TrendingStocks
                    onStockSelect={handleStockSelect}
                    onAddToWatchlist={handleAddToWatchlist}
                  />
                  <MarketMovers
                    onStockSelect={handleStockSelect}
                    onAddToWatchlist={handleAddToWatchlist}
                  />
                  <RecentSearches
                    onStockSelect={handleStockSelect}
                    onSearchSelect={handleSearchSelect}
                  />
                </>
              )}
            </div>
          </div>
        </div>

        {/* Watchlist Success Message */}
        {watchlistMessage && (
          <div className="fixed bottom-4 right-4 bg-success text-success-foreground px-4 py-2 rounded-lg shadow-lg z-alert animate-slide-in">
            <div className="flex items-center space-x-2">
              <Icon name="Check" size={16} />
              <span>{watchlistMessage}</span>
            </div>
          </div>
        )}
      </main>

      {/* Mobile Filter Panel */}
      <FilterPanel
        isOpen={isFilterOpen}
        onClose={() => setIsFilterOpen(false)}
        onApplyFilters={handleApplyFilters}
        currentFilters={filters}
      />

      {/* Stock Detail Modal */}
      <StockDetailModal
        stock={selectedStock}
        isOpen={isStockModalOpen}
        onClose={() => setIsStockModalOpen(false)}
        onAddToWatchlist={handleAddToWatchlist}
      />
    </div>
  );
};

export default StockSearchDiscovery;